import React, { useEffect, useMemo, useState } from 'react';
import type { DropMenuItem } from 'venn-ui-kit';
import { DropMenu, Headline3 } from 'venn-ui-kit';
import { type AnyDuringEslintMigration, plural, useHasFF } from 'venn-utils';
import { DataUploaderMode } from '../types';
import SampleCard from './SampleCard';
import {
  FileLayout,
  FileTypes,
  StrategyEnforcementSampleHistoricalPortfoliosData,
  StrategyEnforcementSamplePortfolioData,
  type Sample,
  SampleFrequency,
  SampleHistoricalPortfoliosData,
  SampleNavData,
  SamplePortfolioData,
  SamplePrivatesData,
  SampleReturnsData,
  TaxAnalysisSampleData,
  StrategyEnforcementTaxAnalysisSampleData,
} from './SampleData';
import { cn } from '@/lib/utils';

const getFiltersForEnum = function <T extends string | number>(
  menuEnum: T,
  allItemsLabel: string,
): DropMenuItem<T[]>[] {
  const values = Object.values(menuEnum);
  return [
    {
      value: values,
      label: allItemsLabel,
    },
    ...values.map((v) => ({
      value: [v],
      label: v,
    })),
  ];
};

const fileTypeItems = getFiltersForEnum<FileTypes>(FileTypes as AnyDuringEslintMigration, 'All File Types');

const layoutTypeItems = getFiltersForEnum<FileLayout>(FileLayout as AnyDuringEslintMigration, 'All Layouts');

const frequencyTypeItems = getFiltersForEnum<SampleFrequency>(
  SampleFrequency as AnyDuringEslintMigration,
  'All Frequencies',
);

const getSampleList = (
  mode: DataUploaderMode,
  hasHistoricals: boolean,
  hasStrategyEnforcement: boolean,
  hasTaxImpact: boolean,
) => {
  switch (mode) {
    case DataUploaderMode.NewNavs:
      return SampleNavData;
    case DataUploaderMode.Privates:
      return SamplePrivatesData;
    case DataUploaderMode.Portfolios:
      if (hasStrategyEnforcement) {
        return hasHistoricals
          ? [
              ...StrategyEnforcementSamplePortfolioData,
              ...StrategyEnforcementSampleHistoricalPortfoliosData,
              ...(hasTaxImpact ? StrategyEnforcementTaxAnalysisSampleData : []),
            ]
          : [
              ...StrategyEnforcementSamplePortfolioData,
              ...(hasTaxImpact ? StrategyEnforcementTaxAnalysisSampleData : []),
            ];
      }
      return hasHistoricals
        ? [...SamplePortfolioData, ...SampleHistoricalPortfoliosData, ...(hasTaxImpact ? TaxAnalysisSampleData : [])]
        : [...SamplePortfolioData, ...(hasTaxImpact ? TaxAnalysisSampleData : [])];
    default:
      return SampleReturnsData;
  }
};

const DataGallery = ({ mode }: { mode: DataUploaderMode; isUniversalUploader?: boolean }) => {
  const hasHistoricals = useHasFF('historical_portfolios_ff');
  const hasStrategyEnforcement = useHasFF('rcm_strategy_enforcement_ff');
  const hasTaxImpact = useHasFF('tax_impact_ff');
  const samples = useMemo(
    () => getSampleList(mode, hasHistoricals, hasStrategyEnforcement, hasTaxImpact),
    [hasHistoricals, mode, hasStrategyEnforcement, hasTaxImpact],
  );
  const [visibleSamples, setVisibleSamples] = useState<Sample[]>(samples);
  const [fileTypeFilter, setFileTypeFiler] = useState<DropMenuItem<FileTypes[]>>(fileTypeItems[0]!);
  const [layoutTypeFilter, setLayoutTypeFilter] = useState<DropMenuItem<FileLayout[]>>(layoutTypeItems[0]!);
  const [frequencyTypeFilter, setFrequencyTypeFilter] = useState<DropMenuItem<SampleFrequency[]>>(
    frequencyTypeItems[0]!,
  );

  useEffect(() => {
    setVisibleSamples(
      samples
        .filter((s) => fileTypeFilter.value.includes(s.type))
        .filter((s) => layoutTypeFilter.value.includes(s.layout))
        .filter((s) => !s.frequency || frequencyTypeFilter.value.some((v) => s.frequency?.includes(v))),
    );
  }, [fileTypeFilter, layoutTypeFilter, frequencyTypeFilter, samples]);

  return (
    <div className="relative w-full h-full border-2 border-solid border-venn-grey" data-testid="data-gallery">
      {mode === DataUploaderMode.Returns && (
        <div
          className={cn(
            'flex items-center h-[60px] w-full bg-venn-grey-10 py-0 px-[20px]',
            // this is necessary to undo styling of TriggerContainer which sets default border color
            '![&_.trigger-container]:focus-within:[&>div:last-child]:border',
            '![&_.trigger-container]:focus-within:[&>div:last-child]:border-solid',
            '![&_.trigger-container]:focus-within:[&>div:last-child]:border-venn-dark-blue',
          )}
        >
          <div className="pr-[10px]">
            <DropMenu items={fileTypeItems} onChange={setFileTypeFiler} selected={fileTypeFilter.value} />
          </div>
          <div className="pr-[10px]">
            <DropMenu items={layoutTypeItems} onChange={setLayoutTypeFilter} selected={layoutTypeFilter.value} />
          </div>
          <div className="pr-[10px]">
            <DropMenu
              items={frequencyTypeItems}
              onChange={setFrequencyTypeFilter}
              selected={frequencyTypeFilter.value}
            />
          </div>
          <div className="text-[10px] text-venn-grey-80 text-right flex-1">
            {plural(visibleSamples.length, {
              1: '{{count}} Available Template',
              other: '{{count}} Available Templates',
            })}
          </div>
        </div>
      )}
      <div
        className={cn([
          'absolute left-0 right-0 bottom-0 pt-[20px] px-[30px] pb-0 overflow-y-auto',
          mode === DataUploaderMode.Returns ? 'top-[60px]' : 'top-0',
        ])}
      >
        {mode === DataUploaderMode.Privates && <Headline3>Download one of the two Excel templates below:</Headline3>}
        {visibleSamples.map((sample) => (
          <SampleCard sample={sample} key={sample.tag} mode={mode} />
        ))}
      </div>
    </div>
  );
};

export default DataGallery;
